/**
 * VIN Decoder — NHTSA API Integration
 * Decodes US vehicle VINs to extract year, make, model, and engine info
 * Uses free NHTSA (National Highway Traffic Safety Administration) API
 */

/**
 * Validate VIN format (basic check — 17 alphanumeric characters)
 */
export function isValidVIN(vin) {
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin?.trim());
}

/**
 * Decode VIN using NHTSA API
 * Returns: { year, make, model, trim, engine, transmission, valid }
 */
export async function decodeVIN(vin) {
  if (!isValidVIN(vin)) {
    return { valid: false, error: 'Invalid VIN format (must be 17 characters)' };
  }

  try {
    const response = await fetch(
      `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin.toUpperCase()}?format=json`
    );

    if (!response.ok) {
      return { valid: false, error: 'Failed to decode VIN' };
    }

    const data = await response.json();

    // Check if NHTSA found a valid VIN
    if (!data.Results || data.Results.length === 0) {
      return { valid: false, error: 'VIN not found in NHTSA database' };
    }

    // Extract relevant fields from NHTSA response
    const results = data.Results;
    const findResult = (variable) => {
      const item = results.find((r) => r.Variable === variable);
      return item ? item.Value : null;
    };

    const year = findResult('Model Year');
    const make = findResult('Make');
    const model = findResult('Model');
    const trim = findResult('Trim') || '';
    const engine = findResult('Engine Displacement (L)');
    const engineCylinders = findResult('Engine Number of Cylinders');
    const transmission = findResult('Transmission') || '';

    // Normalize transmission type
    let transmissionType = '';
    if (transmission) {
      const transLower = transmission.toLowerCase();
      if (transLower.includes('cvt')) {
        transmissionType = 'CVT';
      } else if (transLower.includes('automatic') || transLower.includes('auto')) {
        transmissionType = 'Automatic';
      } else if (transLower.includes('manual')) {
        transmissionType = 'Manual';
      } else {
        transmissionType = transmission.trim();
      }
    }

    // Validate we got the essentials
    if (!year || !make || !model) {
      return {
        valid: false,
        error: 'Could not extract vehicle details from VIN'
      };
    }

    return {
      valid: true,
      vin: vin.toUpperCase(),
      year: parseInt(year),
      make: make.trim(),
      model: model.trim(),
      trim: trim ? trim.trim() : '',
      engine: engine ? `${engine}L` : engineCylinders ? `${engineCylinders}-Cylinder` : '',
      transmission: transmissionType,
      raw: data.Results, // Store full response for debugging
    };
  } catch (error) {
    console.error('VIN Decoder error:', error);
    return {
      valid: false,
      error: 'Network error — unable to decode VIN'
    };
  }
}
